import React, { Component, isValidElement } from 'react';
import { findDOMNode } from 'react-dom';
import classNames from 'classnames';
import noop from 'lodash/noop';
import Checkbox from '../checkbox';
import Search from './search';
import Item from './item';
import triggerEvent from '../_util/triggerEvent';
import Animate from '../animate';
import PureRenderMixin from '../rc-components/util/PureRenderMixin';
function isIEorEDGE() {
    return document.documentMode || /Edge/.test(navigator.userAgent);
}
function isRenderResultPlainObject(result) {
    return (result &&
        !isValidElement(result) &&
        Object.prototype.toString.call(result) === '[object Object]');
}
export default class TransferList extends Component {
    constructor() {
        super(...arguments);
        this.state = {
            mounted: false,
        };
        this.handleSelect = (selectedItem) => {
            const { checkedKeys, handleSelect } = this.props;
            const result = checkedKeys.some(key => key === selectedItem.key);
            handleSelect(selectedItem, !result);
        };
        this.handleFilter = (e) => {
            const { handleFilter, prefixCls } = this.props;
            handleFilter(e);
            if (!e.target.value) {
                return;
            }
            // Manually trigger scroll event for lazy search bug
            this.triggerScrollTimer = window.setTimeout(() => {
                const transferNode = findDOMNode(this);
                const listNode = transferNode.querySelectorAll(`.${prefixCls}-content`)[0];
                if (listNode) {
                    triggerEvent(listNode, 'scroll');
                }
            }, 0);
            this.fixIERepaint();
        };
        this.handleClear = () => {
            const { handleClear } = this.props;
            handleClear();
            this.fixIERepaint();
        };
        this.matchFilter = (text, item) => {
            const { filter, filterOption } = this.props;
            if (filterOption) {
                return filterOption(filter, item);
            }
            return text.indexOf(filter) >= 0;
        };
        this.renderItem = (item) => {
            const { render = noop } = this.props;
            const renderResult = render(item);
            const isRenderResultPlain = isRenderResultPlainObject(renderResult);
            return {
                renderedText: isRenderResultPlain ? renderResult.value : renderResult,
                renderedEl: isRenderResultPlain ? renderResult.label : renderResult,
            };
        };
        this.saveNotFoundRef = (node) => {
            this.notFoundNode = node;
        };
    }
    componentDidMount() {
        this.timer = window.setTimeout(() => {
            this.setState({
                mounted: true,
            });
        }, 0);
    }
    componentWillUnmount() {
        clearTimeout(this.timer);
        clearTimeout(this.triggerScrollTimer);
        clearTimeout(this.fixIERepaintTimer);
    }
    shouldComponentUpdate(...args) {
        return PureRenderMixin.shouldComponentUpdate.apply(this, args);
    }
    getCheckStatus(filteredDataSource) {
        const { checkedKeys } = this.props;
        if (checkedKeys.length === 0) {
            return 'none';
        }
        if (filteredDataSource.every(item => checkedKeys.indexOf(item.key) >= 0)) {
            return 'all';
        }
        return 'part';
    }
    // Fix IE/Edge repaint
    fixIERepaint() {
        if (!isIEorEDGE()) {
            return;
        }
        this.fixIERepaintTimer = window.setTimeout(() => {
            if (this.notFoundNode) {
                const { className } = this.notFoundNode;
                this.notFoundNode.className = className;
            }
        }, 0);
    }
    render() {
        const { prefixCls, checkboxPrefixCls, dataSource, titleText, checkedKeys, highlightKey, lazy, body = noop, footer = noop, showSearch, style, filter, searchPlaceholder, notFoundContent, itemUnit, itemsUnit, onScroll, handleSelectAll, inputProps, } = this.props;
        const { mounted } = this.state;
        // Custom Layout
        const footerDom = footer({ ...this.props });
        const bodyDom = body({ ...this.props });
        const listCls = classNames(prefixCls, {
            [`${prefixCls}-with-footer`]: !!footerDom,
        });
        const filteredDataSource = [];
        const totalDataSource = [];
        const showItems = dataSource.map(item => {
            const { renderedText, renderedEl } = this.renderItem(item);
            if (filter && filter.trim() && !this.matchFilter(renderedText, item)) {
                return null;
            }
            // all show items
            totalDataSource.push(item);
            if (!item.disabled) {
                // response to checkAll items
                filteredDataSource.push(item);
            }
            const checked = checkedKeys.indexOf(item.key) >= 0;
            const isHighlight = highlightKey === item.key;
            return (React.createElement(Item, { key: item.key, item: item, lazy: lazy, renderedText: renderedText, renderedEl: renderedEl, checked: checked, isHighlight: isHighlight, prefixCls: prefixCls, onClick: this.handleSelect, checkboxPrefixCls: checkboxPrefixCls }));
        });
        const unit = dataSource.length > 1 ? itemsUnit : itemUnit;
        const search = showSearch ? (React.createElement("div", { className: `${prefixCls}-body-search-wrapper` },
            React.createElement(Search, { prefixCls: `${prefixCls}-search`, onChange: this.handleFilter, handleClear: this.handleClear, placeholder: searchPlaceholder, value: filter, inputProps: inputProps }))) : null;
        const listBody = bodyDom || (React.createElement("div", { className: showSearch ? `${prefixCls}-body ${prefixCls}-body-with-search` : `${prefixCls}-body` },
            search,
            React.createElement(Animate, { component: "ul", componentProps: { onScroll }, className: `${prefixCls}-content`, transitionName: mounted ? `${prefixCls}-content-item-highlight` : '', transitionLeave: false }, showItems),
            React.createElement("div", { className: `${prefixCls}-body-not-found`, ref: this.saveNotFoundRef }, notFoundContent)));
        const listFooter = footerDom ? React.createElement("div", { className: `${prefixCls}-footer` }, footerDom) : null;
        const checkStatus = this.getCheckStatus(filteredDataSource);
        const checkedAll = checkStatus === 'all';
        const checkAllCheckbox = (React.createElement(Checkbox, { prefixCls: checkboxPrefixCls, checked: checkedAll, indeterminate: checkStatus === 'part', onChange: () => handleSelectAll(filteredDataSource, checkedAll) }));
        return (React.createElement("div", { className: listCls, style: style },
            React.createElement("div", { className: `${prefixCls}-header` },
                checkAllCheckbox,
                React.createElement("span", { className: `${prefixCls}-header-selected` },
                    React.createElement("span", null,
                        (checkedKeys.length > 0 ? `${checkedKeys.length}/` : '') + totalDataSource.length,
                        ' ',
                        unit),
                    React.createElement("span", { className: `${prefixCls}-header-title` }, titleText))),
            listBody,
            listFooter));
    }
}
TransferList.displayName = 'TransferList';
TransferList.defaultProps = {
    dataSource: [],
    titleText: '',
    showSearch: false,
    render: noop,
    lazy: {},
};
